// Immersive hands-free voice conversation overlay (VOICE_AI_DESIGN — live mode). A full-
// screen surface with one reactive ORB that shows the conversation phase and breathes with
// your voice, your last utterance, and the model's reply streaming back token-by-token.
// The loop itself lives in useVoiceConversation; this is the face of it. Tap the orb to
// barge in / skip; End (or Esc) closes.

import { useEffect, useRef } from 'react';
import { useVoiceConversation, type VoicePhase } from './useVoiceConversation';
import { cleanForSpeech } from './voice';

interface VoiceModeProps {
  open: boolean;
  /** Chat's in-flight flag. */
  streaming: boolean;
  /** The last finalized assistant message — spoken, and shown streaming as a caption. */
  lastAssistant: { id: string; content: string } | null;
  /** Send a user turn (Chat.tsx send). Fire-and-forget. */
  onSend: (text: string) => void;
  /** Stop the current turn (Chat.tsx stop) — for barge-in while thinking. */
  onStop?: (() => void) | undefined;
  /** On barge-in, rewrite the last assistant message to only what was spoken (context truncation). */
  onTruncate?: ((spokenText: string) => void) | undefined;
  /** Close the overlay. */
  onClose: () => void;
}

const STATUS: Record<VoicePhase, string> = {
  idle: '', listening: 'Listening…', transcribing: 'Got that…',
  thinking: 'Thinking…', speaking: 'Speaking…', error: 'Microphone needed', muted: 'Muted',
};
const HINT: Record<VoicePhase, string> = {
  idle: '', listening: 'Just start talking — I’ll answer when you pause.',
  transcribing: '', thinking: '', speaking: 'Tap the orb to jump in.', error: '',
  muted: 'Mic is off — tap the orb or the mic button to resume.',
};

export function VoiceMode({ open, streaming, lastAssistant, onSend, onStop, onTruncate, onClose }: VoiceModeProps) {
  const { phase, transcript, level, error, muted, heard, endpointProgress, interrupt, submitTurn, toggleMute, retry } = useVoiceConversation({
    active: open, streaming, lastAssistant, onSend, onStop, onTruncate, onAutoExit: onClose,
  });

  const overlayRef = useRef<HTMLDivElement>(null);
  const orbRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<Element | null>(null);
  // onClose is a fresh inline arrow on every Chat render; read it through a ref so the
  // focus/keydown effect keys ONLY on `open` and doesn't re-run (thrashing focus) on every
  // streamed reply token.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Esc closes; Tab is trapped within the overlay; focus moves in on open and restores on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement;
    const focusOrb = window.setTimeout(() => orbRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const root = overlayRef.current;
      if (!root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>('button:not([disabled])'));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const activeEl = document.activeElement;
      if (e.shiftKey && activeEl === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && activeEl === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusOrb);
      window.removeEventListener('keydown', onKey);
      const el = restoreFocusRef.current;
      if (el instanceof HTMLElement) el.focus();
    };
  }, [open]);

  if (!open) return null;

  // The orb "breathes" with the live mic level while listening; other phases animate via CSS.
  const reactiveScale = phase === 'listening' ? 1 + Math.min(0.28, level * 0.28) : 1;
  // Live caption: the reply is being SPOKEN, so the screen shouldn't become a wall of text.
  // Strip markdown (no stray ** or list numbers read as clutter) and show only the most recent
  // words — a caption that flows with the voice, not a document to read.
  const cleanReply = (phase === 'thinking' || phase === 'speaking') ? cleanForSpeech(lastAssistant?.content || '') : '';
  let displayReply = cleanReply;
  if (cleanReply.length > 220) {
    const tail = cleanReply.slice(-220);
    const sp = tail.indexOf(' ');            // drop the leading partial word after the cut
    displayReply = '…' + (sp > 0 ? tail.slice(sp) : tail);
  }
  // Tapping the orb WHILE LISTENING submits your turn now (was a data-loss no-op) — instant
  // "answer now" without waiting on the silence timer. Other phases keep their meaning.
  const orbAction = phase === 'error' ? retry : phase === 'muted' ? toggleMute : phase === 'listening' ? submitTurn : interrupt;
  const orbLabel = phase === 'error' ? 'Retry microphone' : phase === 'muted' ? 'Unmute and resume'
    : phase === 'listening' ? 'Tap when you’re done talking' : 'Tap to jump in';
  // The listening hint reassures long/paused talkers that they won't be cut off.
  const listeningHint = !heard
    ? 'Just start talking — take your time, I’ll wait.'
    : endpointProgress > 0.15 ? 'Answering in a moment — keep talking to continue.' : 'I’m listening — keep going.';

  return (
    <div className="voicemode" role="dialog" aria-modal="true" aria-label="Voice conversation" ref={overlayRef}>
      <button type="button" className="voicemode__close" onClick={onClose} aria-label="End voice conversation">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      <div className="voicemode__stage">
        <button
          type="button"
          ref={orbRef}
          className={`voicemode__orb voicemode__orb--${phase}`}
          style={{ ['--orb-scale' as string]: reactiveScale.toFixed(3) }}
          onClick={orbAction}
          aria-label={orbLabel}
        >
          <span className="voicemode__orb-core" />
          <span className="voicemode__orb-ring" />
          <span className="voicemode__orb-ring voicemode__orb-ring--2" />
          {phase === 'listening' && endpointProgress > 0 ? (
            <span className="voicemode__orb-endpoint" style={{ ['--endpoint' as string]: endpointProgress.toFixed(2) }} aria-hidden="true" />
          ) : null}
        </button>

        {/* Live mic level — visible proof it's hearing you. */}
        {phase === 'listening' ? (
          <div className="voicemode__meter" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((k) => {
              const h = Math.max(0, Math.min(1, level * (1 + Math.abs(k - 2) * 0.15) - Math.abs(k - 2) * 0.04));
              return <span key={k} style={{ ['--h' as string]: h.toFixed(2) }} />;
            })}
          </div>
        ) : null}

        <div className="voicemode__status">{error ? error : STATUS[phase]}</div>
        {/* One polite live-region: announce only the recognized transcript / error, not every phase flip. */}
        <div className="sr-only" aria-live="polite">{error || (transcript ? `Heard: ${transcript}` : '')}</div>
        {!error ? (
          <div className="voicemode__hint">{phase === 'listening' ? listeningHint : HINT[phase]}</div>
        ) : null}

        {transcript && phase !== 'error' ? (
          <div className="voicemode__caption" title="What I heard">“{transcript}”</div>
        ) : null}
        {displayReply ? (
          <div className="voicemode__caption voicemode__caption--assistant" aria-hidden="true">{displayReply}</div>
        ) : null}

        {phase === 'error' ? (
          <button type="button" className="voicemode__retry" onClick={retry}>Try again</button>
        ) : null}
      </div>

      <div className="voicemode__footer">
        <button
          type="button"
          className={`voicemode__mic${muted ? ' is-muted' : ''}`}
          onClick={toggleMute}
          aria-pressed={muted}
          aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 2l20 20" />
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23M12 19v3" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" />
            </svg>
          )}
        </button>
        {phase === 'listening' && heard ? (
          <button type="button" className="voicemode__skip" onClick={submitTurn}>Done</button>
        ) : null}
        {phase === 'speaking' || phase === 'thinking' ? (
          <button type="button" className="voicemode__skip" onClick={interrupt}>
            {phase === 'speaking' ? 'Interrupt' : 'Skip'}
          </button>
        ) : null}
        <button type="button" className="voicemode__end" onClick={onClose}>End</button>
      </div>
    </div>
  );
}
